import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useThemeColors } from '../../hooks/useThemeColors'
import { OrganizationSwitcher } from '../OrganizationSwitcher'
import { ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'

type NavigationMode = 'platform' | 'organization'

interface NavItem {
  label: string
  href: string
  badge?: number
}

interface NavSection {
  title: string
  items: NavItem[]
}

interface SuperAdminSidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
}

export function SuperAdminSidebar({ isCollapsed, onToggleCollapse }: SuperAdminSidebarProps) {
  const { organization } = useOrganization()
  const location = useLocation()
  const [mode, setMode] = useState<NavigationMode>(() => {
    const isPlatformRoute = location.pathname.startsWith('/platform')
    return isPlatformRoute ? 'platform' : 'organization'
  })
  const [expandedItems, setExpandedItems] = useState<string[]>(['main', 'operations', 'management', 'settings', 'organizations', 'billing & subscriptions', 'platform settings'])
  
  // Force refresh colors based on current mode
  const { colors } = useThemeColors()

  // Update mode when location changes
  useEffect(() => {
    const isPlatformRoute = location.pathname.startsWith('/platform') || location.pathname.startsWith('/admin')
    const storedMode = localStorage.getItem('super-admin-mode')
    
    // Prioritize route-based detection, then stored mode
    if (isPlatformRoute) {
      setMode('platform')
    } else if (storedMode === 'platform' || storedMode === 'organization') {
      setMode(storedMode as NavigationMode)
    }
  }, [location.pathname])

  useEffect(() => {
    localStorage.setItem('superAdminNavMode', mode)
  }, [mode])

  const toggleExpanded = (section: string) => {
    setExpandedItems(prev => 
      prev.includes(section) 
        ? prev.filter(item => item !== section)
        : [...prev, section]
    )
  }

  // Platform navigation
  const platformNavigation: NavSection[] = [
    {
      title: 'ORGANIZATIONS',
      items: [
        { label: 'All Organizations', href: '/platform/organizations' },
        { label: 'Organization Users', href: '/platform/organization-users' },
        { label: 'Create Organization', href: '/platform/organizations/new' },
        { label: 'Usage Analytics', href: '/platform/analytics' },
      ]
    },
    {
      title: 'BILLING & REVENUE',
      items: [
        { label: 'Organization Billing', href: '/platform/billing' },
        { label: 'Subscription Plans', href: '/platform/subscription-plans' },
        { label: 'Revenue Dashboard', href: '/platform/revenue' },
        { label: 'Payment History', href: '/platform/payments' },
      ]
    },
    {
      title: 'PLATFORM ADMIN',
      items: [
        { label: 'Global Settings', href: '/platform/settings' },
        { label: 'Feature Flags', href: '/platform/features' },
        { label: 'Email Templates', href: '/platform/templates' },
        { label: 'API Management', href: '/platform/api' },
      ]
    },
    {
      title: 'MONITORING',
      items: [
        { label: 'System Health', href: '/platform/health' },
        { label: 'Audit Logs', href: '/platform/audit' },
        { label: 'Error Tracking', href: '/platform/errors' },
        { label: 'Performance', href: '/platform/performance' },
      ]
    }
  ]

  // Organization navigation
  const organizationNavigation: NavSection[] = [
    {
      title: 'MAIN',
      items: [
        { label: 'Dashboard', href: '/' },
        { label: 'Schedule', href: '/schedule' },
        { label: 'Clients', href: '/clients' },
        { label: 'Programs & Classes', href: '/booking-types' },
      ]
    },
    {
      title: 'OPERATIONS',
      items: [
        { label: 'Facilities', href: '/facilities' },
        { label: 'Invoicing', href: '/invoices' },
        { label: 'Packages', href: '/packages' },
        { label: 'Waivers', href: '/waivers' },
      ]
    },
    {
      title: 'SETTINGS',
      items: [
        { label: 'General', href: '/settings/general' },
        { label: 'Team Management', href: '/team' },
        { label: 'Billing', href: '/settings/billing' },
        { label: 'Email Templates', href: '/settings/email' },
      ]
    }
  ]

  const navigationSections = mode === 'platform' ? platformNavigation : organizationNavigation

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div 
      className={`sidebar admin-sidebar ${isCollapsed ? 'collapsed' : ''}`} 
      style={{
        backgroundColor: colors.backgroundDark,
        borderRight: `2px solid ${colors.border}`,
        width: isCollapsed ? '60px' : '260px',
        minWidth: isCollapsed ? '60px' : '260px',
        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.1)',
        transition: 'width 0.3s ease'
      }}
    >
      {/* Collapse Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className="sidebar-toggle"
        style={{
          position: 'absolute',
          right: '10px',
          top: '20px',
          width: '28px',
          height: '28px',
          borderRadius: '8px',
          backgroundColor: colors.backgroundDark,
          border: `1px solid ${colors.border}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          color: colors.primary
        }}
      >
        {isCollapsed ? <ChevronRightIcon style={{ width: '16px', height: '16px' }} /> : <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />}
      </button>

      {/* Logo Section with Mode Toggle */}
      <div className="logo-section" style={{
        padding: isCollapsed ? '1.5rem 0.5rem 2rem' : '1.5rem 1.25rem 2rem',
        borderBottom: `1px solid ${colors.border}`,
        marginBottom: '1.5rem',
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        <div className="dashboard-logo" style={{ 
          color: mode === 'platform' ? '#6B9BD1' : colors.primary,
          fontSize: isCollapsed ? '1.2rem' : '1.5rem',
          fontWeight: '800',
          marginBottom: '0.5rem',
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap'
        }}>
          {isCollapsed ? (mode === 'platform' ? 'FM' : 'SA') : (mode === 'platform' ? 'Facility Manager' : 'Super Admin')}
        </div>
        
        {!isCollapsed && (
          <>
            <div className="logo-subtitle" style={{ 
              color: mode === 'platform' ? '#6B9BD1' : colors.primary,
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '1rem',
              opacity: 0.8
            }}>
              Platform Management
            </div>

            {/* Compact Mode Selector */}
            <div style={{ 
              marginBottom: mode === 'organization' ? '0.75rem' : '0'
            }}>
              <select
                value={mode}
                onChange={(e) => {
                  const newMode = e.target.value as NavigationMode
                  setMode(newMode)
                  localStorage.setItem('super-admin-mode', newMode)
                  
                  // Navigate based on mode
                  if (newMode === 'platform') {
                    window.location.href = '/platform/organizations'
                  } else {
                    // Clear platform mode and reload to get organization context
                    window.location.href = '/'
                  }
                }}
                className="form-select"
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  fontSize: '0.875rem',
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  color: colors.text,
                  cursor: 'pointer',
                  fontWeight: '500',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = colors.primary
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primaryAlpha20}`
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = colors.border
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <option value="platform">Platform Admin</option>
                <option value="organization">Organization Admin</option>
              </select>
            </div>

            {/* Organization Selector when in org mode */}
            {mode === 'organization' && organization && (
              <OrganizationSwitcher />
            )}
          </>
        )}
      </div>

      {/* Navigation Sections */}
      {navigationSections.map((section) => {
        const isExpanded = expandedItems.includes(section.title.toLowerCase())
        
        return (
          <div key={section.title} className="nav-section" style={{
            marginBottom: '0.5rem',
            padding: '0 0.625rem'
          }}>
            {/* Section Header */}
            {!isCollapsed && (
              <button
                onClick={() => toggleExpanded(section.title.toLowerCase())}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  marginBottom: isExpanded ? '0.5rem' : '0'
                }}
              >
                <span style={{ 
                  color: colors.primary,
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}>{section.title}</span>
                <span style={{ 
                  fontSize: '12px',
                  color: colors.textMuted
                }}>
                  ▼
                </span>
              </button>
            )}

            {/* Section Items */}
            {(isExpanded || isCollapsed) && (
              <div style={{ paddingLeft: isCollapsed ? '0' : '0.5rem' }}>
                {section.items.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="admin-nav-item"
                    title={isCollapsed ? item.label : ''}
                    style={{
                      backgroundColor: 'transparent',
                      color: isActive(item.href) ? colors.primary : colors.text,
                      borderLeft: isActive(item.href) ? `3px solid ${colors.primary}` : '3px solid transparent',
                      paddingTop: '0.875rem',
                      paddingRight: '1rem',
                      paddingBottom: '0.875rem',
                      paddingLeft: isActive(item.href) ? 'calc(1.25rem - 3px)' : '1.25rem',
                      fontSize: '0.975rem',
                      fontWeight: isActive(item.href) ? '700' : '600',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '0.5rem',
                      margin: '0.125rem 0',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive(item.href)) {
                        e.currentTarget.style.backgroundColor = colors.primaryAlpha10
                        e.currentTarget.style.color = colors.primary
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive(item.href)) {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.color = colors.text
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    {!isCollapsed && (
                      <span style={{ letterSpacing: '0.02em', fontWeight: 'inherit' }}>{item.label}</span>
                    )}
                    {isCollapsed && (
                      <span style={{ fontSize: '1.375rem', fontWeight: 'inherit' }}>{item.label.charAt(0)}</span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )
      })}

    </div>
  )
}